import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, User as UserIcon, KeyRound, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

function ProfileForm() {
    const { user, updateProfile } = useAuth();
    const [fullName, setFullName] = useState(user.full_name);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const disabled = !user.is_active;
    const changed = fullName.trim() !== user.full_name && fullName.trim().length >= 2;

    const save = async (e) => {
        e.preventDefault();
        setErr("");
        setSaving(true);
        try {
            await updateProfile({ full_name: fullName.trim() });
            toast.success("Nome aggiornato");
        } catch (e2) {
            setErr(apiError(e2));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="border-slate-200" data-testid="profile-info-card">
            <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary font-semibold">
                    <UserIcon className="size-4" />
                    <span className="text-xs uppercase tracking-widest">I tuoi dati</span>
                </div>
            </CardHeader>
            <CardContent>
                <form onSubmit={save} className="space-y-4">
                    {err && (
                        <Alert variant="destructive">
                            <AlertDescription>{err}</AlertDescription>
                        </Alert>
                    )}
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="fullName">Nome e cognome</Label>
                            <Input
                                id="fullName"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                disabled={disabled}
                                data-testid="profile-full-name"
                                maxLength={80}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                value={user.email}
                                disabled
                                data-testid="profile-email"
                            />
                            <p className="text-xs text-muted-foreground">
                                L'email non è modificabile in questa fase.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                        <Badge
                            variant="outline"
                            className={
                                user.role === "admin"
                                    ? "border-primary/40 text-primary"
                                    : "border-slate-300"
                            }
                        >
                            Ruolo: {user.role === "admin" ? "Amministratore" : "Utente"}
                        </Badge>
                        <Badge
                            variant="outline"
                            className={
                                user.is_active
                                    ? "border-emerald-300 text-emerald-700"
                                    : "border-destructive/40 text-destructive"
                            }
                        >
                            {user.is_active ? "Account attivo" : "Account sospeso"}
                        </Badge>
                    </div>
                    <div className="flex justify-end pt-2">
                        <Button
                            type="submit"
                            data-testid="profile-save-btn"
                            disabled={!changed || saving || disabled}
                        >
                            {saving && <Loader2 className="size-4 mr-2 animate-spin" />}
                            Salva modifiche
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}

function PasswordForm() {
    const { user, changePassword } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const disabled = !user.is_active;
    const mismatch = confirm.length > 0 && next !== confirm;
    const ok = current && next.length >= 8 && next === confirm && !disabled;

    const save = async (e) => {
        e.preventDefault();
        setErr("");
        setSaving(true);
        try {
            await changePassword({ current_password: current, new_password: next });
            toast.success("Password aggiornata");
            setCurrent("");
            setNext("");
            setConfirm("");
        } catch (e2) {
            setErr(apiError(e2));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="border-slate-200" data-testid="profile-password-card">
            <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary font-semibold">
                    <KeyRound className="size-4" />
                    <span className="text-xs uppercase tracking-widest">Cambio password</span>
                </div>
            </CardHeader>
            <CardContent>
                <form onSubmit={save} className="space-y-4">
                    {err && (
                        <Alert variant="destructive">
                            <AlertDescription>{err}</AlertDescription>
                        </Alert>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="current">Password attuale</Label>
                        <Input
                            id="current"
                            type="password"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            disabled={disabled}
                            data-testid="profile-current-password"
                            autoComplete="current-password"
                        />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="next">Nuova password</Label>
                            <Input
                                id="next"
                                type="password"
                                value={next}
                                onChange={(e) => setNext(e.target.value)}
                                disabled={disabled}
                                minLength={8}
                                data-testid="profile-new-password"
                                autoComplete="new-password"
                            />
                            <p className="text-xs text-muted-foreground">
                                Almeno 8 caratteri, 1 lettera e 1 numero.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirm">Conferma password</Label>
                            <Input
                                id="confirm"
                                type="password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                disabled={disabled}
                                data-testid="profile-confirm-password"
                                autoComplete="new-password"
                            />
                            {mismatch && (
                                <p className="text-xs text-destructive">
                                    Le password non coincidono.
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-end pt-2">
                        <Button
                            type="submit"
                            data-testid="profile-change-password-btn"
                            disabled={!ok || saving}
                        >
                            {saving && <Loader2 className="size-4 mr-2 animate-spin" />}
                            Aggiorna password
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}

export default function Profile() {
    const { user } = useAuth();
    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14 space-y-6">
            <div>
                <h1
                    data-testid="profile-title"
                    className="font-serif text-3xl lg:text-4xl font-semibold"
                >
                    Il mio profilo
                </h1>
                <p className="mt-2 text-muted-foreground">
                    Modifica il tuo nome o cambia la password del tuo account.
                </p>
            </div>
            {!user.is_active && (
                <Alert variant="destructive" data-testid="profile-suspended">
                    <ShieldAlert className="size-4" />
                    <AlertDescription>
                        Il tuo account è sospeso. Non puoi modificare i dati. Contatta l'assistenza.
                    </AlertDescription>
                </Alert>
            )}
            <ProfileForm />
            <PasswordForm />
        </div>
    );
}
